import { useState } from 'react';

import type { SubsidyOut, SubsidyRateOut } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync, won } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * [시]/[구] 보조금 관리 — 레거시 define/subsidy/parent, define/subsidy 파리티.
 *
 * 한 테이블(def_subsidy)이 두 종류의 행을 담으므로 화면 하나가 kind 로 갈린다.
 * [구] 등록은 시 보조금 선등록이 강제인데(서버 409 "시 보조금을 먼저 등록해
 * 주십시오."), 검증은 서버가 하고 여기서는 메시지를 그대로 보여준다.
 *
 * gu_state 는 레거시 값 그대로 '1'=지급 / '2'=미지급 (detail.blade.php:47-48,
 * subsidy_service.gu_price_by_name 은 '1' 만 지급으로 본다).
 *
 * 목록 아래에는 2026 공고 단가표(def_subsidy_gu_year 신규 계열)를 읽기 전용으로 붙인다 —
 * 시민 견적이 실제로 쓰는 재원 기준이라, 이 화면의 레거시 금액과 혼동하지 않게 함께 보여준다.
 */

const SIZE = 30;

export function SubsidyManage({ kind }: { kind: 'si' | 'gu' }) {
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const list = useAsync(
    () => api.listSubsidies({ kind, keyword: applied, page, size: SIZE }),
    [kind, applied, page],
  );

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));
  const row = list.data?.items.find((s) => s.id === selected) ?? null;
  const isSi = kind === 'si';

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="지역 이름 · 금액"
            />
            <button type="submit">검색</button>
          </form>
          <button type="button" className="primary create" onClick={() => setCreateOpen(true)}>
            등록
          </button>
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              {isSi ? (
                <tr>
                  <th>지역</th>
                  <th className="num">보조금 금액(500W이하 시)</th>
                  <th className="num">보조금 금액(500W초과 ~999W미만시)</th>
                  <th className="num">SH 임대주택 보조금 금액</th>
                </tr>
              ) : (
                <tr>
                  <th>지역</th>
                  <th className="num">1회 보조금 금액</th>
                  <th className="num">최대 지원 금액</th>
                  <th>보조금 지원 여부</th>
                </tr>
              )}
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    등록된 보조금이 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((s) => (
                <tr
                  key={s.id}
                  className={s.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(s.id)}
                >
                  <td>
                    {isSi
                      ? (s.addr_si_name ?? '-')
                      : [s.addr_si_name, s.addr_gu_name].filter(Boolean).join(' ') || '-'}
                  </td>
                  <td className="num">{won(s.amount)}</td>
                  {isSi ? (
                    <>
                      <td className="num">{won(s.amount2)}</td>
                      <td className="num">{won(s.sh_amount)}</td>
                    </>
                  ) : (
                    <>
                      <td className="num">{won(s.max_amount)}</td>
                      <td>{s.gu_state === '1' ? '지급' : '미지급'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>

        <SubsidyRates />
      </section>

      <aside className="admin__aside">
        {row === null ? (
          <p className="muted">보조금을 선택하세요.</p>
        ) : (
          <SubsidyEdit
            key={row.id}
            row={row}
            isSi={isSi}
            onClose={() => setSelected(null)}
            onChanged={() => list.reload()}
            onDeleted={() => {
              setSelected(null);
              list.reload();
            }}
          />
        )}
      </aside>

      {createOpen && (
        <SubsidyCreateModal
          isSi={isSi}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

function toInt(value: string): number | undefined {
  const n = Number(value);
  return value.trim() !== '' && Number.isFinite(n) ? Math.trunc(n) : undefined;
}

const RATE_YEAR = '2026';
const INSTALL_TYPE_LABEL: Record<string, string> = { rail: '난간형', rooftop: '옥상형' };
const VARIANT_LABEL: Record<string, string> = { group: '단체(10가구 이상)', guard: '경비실' };

function rateRegion(r: SubsidyRateOut): string {
  return [r.addr_si ?? '전국', r.addr_gu].filter(Boolean).join(' ');
}

/** 2026 공고 단가표 — def_subsidy_gu_year 신규 계열, 읽기 전용 (시민 견적의 재원 기준). */
function SubsidyRates() {
  const rates = useAsync(() => api.listSubsidyRates(RATE_YEAR), []);

  return (
    <section className="panel">
      <h3>{RATE_YEAR} 공고 단가표 (시민 견적 기준)</h3>
      {rates.loading && <Spinner />}
      {rates.error && <ErrorBox message={rates.error} onRetry={rates.reload} />}
      {rates.data && (
        <table className="grid">
          <thead>
            <tr>
              <th>지역</th>
              <th className="num">용량 상한</th>
              <th>설치유형</th>
              <th>구분</th>
              <th className="num">국비</th>
              <th className="num">시·도</th>
              <th className="num">구·군</th>
              <th>공고</th>
            </tr>
          </thead>
          <tbody>
            {rates.data.length === 0 && (
              <tr>
                <td colSpan={8} className="muted">
                  등록된 단가가 없습니다. (scripts/seed_program_2026)
                </td>
              </tr>
            )}
            {rates.data.map((r) => (
              <tr key={r.id}>
                <td>{rateRegion(r)}</td>
                <td className="num">{`${r.watt_upper ?? 0}W`}</td>
                <td>{r.install_type === null ? '유형 무관' : (INSTALL_TYPE_LABEL[r.install_type] ?? r.install_type)}</td>
                <td>{r.variant === null ? '일반' : (VARIANT_LABEL[r.variant] ?? r.variant)}</td>
                <td className="num">{r.subsidy_gov ? won(r.subsidy_gov) : `${r.gov_unit_price ?? 0}원/W`}</td>
                <td className="num">{r.subsidy_si ? won(r.subsidy_si) : `${r.unit_price ?? 0}원/W`}</td>
                <td className="num">{won(r.subsidy_gu ?? 0)}</td>
                <td>{r.program ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function SubsidyEdit({
  row,
  isSi,
  onClose,
  onChanged,
  onDeleted,
}: {
  row: SubsidyOut;
  isSi: boolean;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [amount, setAmount] = useState(String(row.amount));
  const [amount2, setAmount2] = useState(row.amount2 === null ? '' : String(row.amount2));
  const [shAmount, setShAmount] = useState(row.sh_amount === null ? '' : String(row.sh_amount));
  const [guState, setGuState] = useState(row.gu_state ?? '1');
  const [maxAmount, setMaxAmount] = useState(
    row.max_amount === null ? '' : String(row.max_amount),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function save() {
    if (toInt(amount) === undefined) {
      setError('보조금 금액을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.updateSubsidy(
        row.id,
        isSi
          ? { amount: toInt(amount), amount2: toInt(amount2), sh_amount: toInt(shAmount) }
          : { amount: toInt(amount), gu_state: guState, max_amount: toInt(maxAmount) },
      );
      setOk('저장했습니다.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteSubsidy(row.id);
      onDeleted();
    } catch (err) {
      // 삭제 가드(하위 자치구 보조금 존재 등) 409 메시지를 그대로 보여준다.
      setError(err instanceof ApiError ? err.message : '삭제하지 못했습니다.');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>
          {isSi
            ? (row.addr_si_name ?? `#${row.id}`)
            : [row.addr_si_name, row.addr_gu_name].filter(Boolean).join(' ') || `#${row.id}`}
        </h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel form-grid">
        {isSi ? (
          <>
            <label className="f wide">
              보조금 금액(500W이하 시) *
              <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="f wide">
              보조금 금액(500W초과 ~999W미만시)
              <input type="number" min={0} value={amount2} onChange={(e) => setAmount2(e.target.value)} />
            </label>
            <label className="f wide">
              SH 임대주택 보조금 금액
              <input type="number" min={0} value={shAmount} onChange={(e) => setShAmount(e.target.value)} />
            </label>
          </>
        ) : (
          <>
            <label className="f wide">
              1회 보조금 금액 *
              <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="f wide">
              최대 지원 금액
              <input type="number" min={0} value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
            </label>
            <label className="f wide">
              보조금 지원 여부
              <select value={guState} onChange={(e) => setGuState(e.target.value)}>
                <option value="1">지급</option>
                <option value="2">미지급</option>
              </select>
            </label>
          </>
        )}
      </section>

      {error && <p className="error">{error}</p>}
      {ok && <p className="ok">{ok}</p>}

      <div className="row form-actions">
        <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
          삭제
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      {confirmDelete && (
        <Modal title="보조금 삭제" onClose={() => setConfirmDelete(false)}>
          <p>보조금 정보를 삭제합니다. 계속할까요?</p>
          <div className="row">
            <button type="button" onClick={() => setConfirmDelete(false)}>
              취소
            </button>
            <button type="button" className="primary" disabled={busy} onClick={() => void remove()}>
              삭제
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SubsidyCreateModal({
  isSi,
  onClose,
  onCreated,
}: {
  isSi: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  // [구] 등록: 시 선택은 [시]보조금 등록분에서 고른다(레거시 SubsidyGuList 흐름).
  const siList = useAsync(
    () => (isSi ? Promise.resolve(null) : api.listSubsidies({ kind: 'si', size: 200 })),
    [isSi],
  );
  // [시] 등록: 시/도 마스터에서 고른다 (GET /define/addr-si).
  const addrSiList = useAsync(
    () => (isSi ? api.listAddrSi() : Promise.resolve(null)),
    [isSi],
  );
  const [addrSiId, setAddrSiId] = useState('');
  const [addrGuId, setAddrGuId] = useState('');
  const guCandidates = useAsync(
    () =>
      !isSi && addrSiId !== '' ? api.guCandidates(Number(addrSiId)) : Promise.resolve(null),
    [isSi, addrSiId],
  );

  const [amount, setAmount] = useState('');
  const [amount2, setAmount2] = useState('');
  const [shAmount, setShAmount] = useState('');
  const [guState, setGuState] = useState('1');
  const [maxAmount, setMaxAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (toInt(addrSiId) === undefined) {
      setError('지역(시/도)을 선택해 주세요.');
      return;
    }
    if (!isSi && toInt(addrGuId) === undefined) {
      setError('지역(구/군)을 선택해 주세요.');
      return;
    }
    if (toInt(amount) === undefined) {
      setError('보조금 금액을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createSubsidy(
        isSi
          ? {
              addr_si_id: toInt(addrSiId),
              amount: toInt(amount) as number,
              amount2: toInt(amount2),
              sh_amount: toInt(shAmount),
            }
          : {
              addr_gu_id: toInt(addrGuId),
              amount: toInt(amount) as number,
              gu_state: guState,
              max_amount: toInt(maxAmount),
            },
      );
      onCreated();
    } catch (err) {
      // "시 보조금을 먼저 등록해 주십시오." / 중복 등록 등 서버 메시지 그대로.
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={isSi ? '[시]보조금 등록' : '[구]보조금 등록'} onClose={onClose}>
      {isSi ? (
        <>
          {addrSiList.error && <ErrorBox message={addrSiList.error} onRetry={addrSiList.reload} />}
          <label className="labeled">
            지역(시/도) *
            <select value={addrSiId} onChange={(e) => setAddrSiId(e.target.value)}>
              <option value="">선택</option>
              {(addrSiList.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : (
        <>
          {siList.error && <ErrorBox message={siList.error} onRetry={siList.reload} />}
          <label className="labeled">
            지역(시/도) *
            <select
              value={addrSiId}
              onChange={(e) => {
                setAddrSiId(e.target.value);
                setAddrGuId('');
              }}
            >
              <option value="">선택</option>
              {(siList.data?.items ?? []).map((s) => (
                <option key={s.addr_si_id} value={s.addr_si_id}>
                  {s.addr_si_name ?? s.addr_si_id}
                </option>
              ))}
            </select>
          </label>
          {guCandidates.error && (
            <ErrorBox message={guCandidates.error} onRetry={guCandidates.reload} />
          )}
          <label className="labeled">
            지역(구/군) *
            <select value={addrGuId} onChange={(e) => setAddrGuId(e.target.value)}>
              <option value="">
                {addrSiId === '' ? '시/도를 먼저 선택하세요' : '선택 (미등록 자치구만)'}
              </option>
              {(guCandidates.data ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      <label className="labeled">
        {isSi ? '보조금 금액(500W이하 시) *' : '1회 보조금 금액 *'}
        <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </label>
      {isSi ? (
        <>
          <label className="labeled">
            보조금 금액(500W초과 ~999W미만시)
            <input type="number" min={0} value={amount2} onChange={(e) => setAmount2(e.target.value)} />
          </label>
          <label className="labeled">
            SH 임대주택 보조금 금액
            <input type="number" min={0} value={shAmount} onChange={(e) => setShAmount(e.target.value)} />
          </label>
        </>
      ) : (
        <>
          <label className="labeled">
            최대 지원 금액
            <input type="number" min={0} value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
          </label>
          <label className="labeled">
            보조금 지원 여부
            <select value={guState} onChange={(e) => setGuState(e.target.value)}>
              <option value="1">지급</option>
              <option value="2">미지급</option>
            </select>
          </label>
        </>
      )}

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
